export { parseOptionalNumber } from '../../lib/parseOptionalNumber';
import { z } from 'zod';

import type {
  LiverampSegment,
  PersistedTargeting,
  PersistedTargetingDeviceType,
  TargetingLocation,
  ZipCodeTargeting,
} from './targetingTypes';

const persistedLocationIdSchema = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((value) => (value == null ? undefined : String(value)));

const persistedLiverampSegmentSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  segmentId: z.union([z.string(), z.number()]).transform(String).optional(),
  segmentName: z.string().optional(),
  providerName: z.string().optional(),
  description: z.string().nullish(),
  segmentDescription: z.string().nullish(),
  useRestrictions: z.string().nullish(),
  permittedAdvertisers: z.string().nullish(),
  customSegment: z.boolean().optional(),
  digitalCpm: z.number().nullish(),
  tvCpm: z.number().nullish(),
});

export function targetingDeviceTypeId(deviceType: PersistedTargetingDeviceType) {
  return deviceType.deviceTypeId ?? deviceType.id ?? '';
}

export function legacyIds(value: unknown[] | undefined) {
  return (value ?? [])
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'id' in item) return String(item.id);
      return '';
    })
    .filter(Boolean);
}

export function normalizeLegacyExcludedItems(value: unknown[] | undefined) {
  return (value ?? []).map((item) => ({
    excluded:
      item !== null && typeof item === 'object' && 'excluded' in item && item.excluded === true,
  }));
}

export function splitLines(value: string) {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function normalizeContextualSelection(
  includedValues: readonly { name: string }[] | undefined,
  excludedValues: readonly { name: string }[] | undefined,
) {
  const selections = [
    ...(includedValues ?? []).map((item) => ({ ...item, excluded: false })),
    ...(excludedValues ?? []).map((item) => ({ ...item, excluded: true })),
  ];
  return {
    included: resolveHomogeneousInclusion(selections, 'content'),
    names: selections.map((item) => item.name).filter(Boolean),
  };
}

export function contextualValues(value: string, active: boolean, excluded = false) {
  return active ? splitLines(value).map((name) => ({ name, excluded })) : [];
}

export function contextualNames(values: readonly string[], active: boolean, excluded = false) {
  return active
    ? values
        .map((name) => name.trim())
        .filter(Boolean)
        .map((name) => ({ name, excluded }))
    : [];
}

export function resolveHomogeneousInclusion(
  items: readonly { excluded: boolean }[],
  collectionName: string,
) {
  const exclusionStates = new Set(items.map((item) => item.excluded));
  if (exclusionStates.size > 1) {
    throw new Error(`Mixed included and excluded ${collectionName} targeting is not supported`);
  }
  return items.length ? !items[0]?.excluded : true;
}

export function splitZipCodes(value: string, excluded: boolean): ZipCodeTargeting[] {
  return splitLines(value)
    .filter((zipCode) => zipCode.length >= 5)
    .map((zipCode) => ({ zipCode, excluded }));
}

export function stringifyNumber(value: number | null | undefined) {
  return value == null || !Number.isFinite(value) ? '' : String(value);
}

export function stripSupplyAppDisplayMetadata(apps: readonly unknown[]) {
  return apps.map((app) => {
    if (!app || typeof app !== 'object') return app;
    const persisted = { ...(app as Record<string, unknown>) };
    delete persisted['iabCategoryNames'];
    return persisted;
  });
}

export function normalizePersistedDomains(targeting: PersistedTargeting) {
  if (targeting.includedDomains || targeting.excludedDomains) {
    return {
      included: (targeting.includedDomains ?? []).map(normalizePersistedDomain),
      excluded: (targeting.excludedDomains ?? []).map(normalizePersistedDomain),
    };
  }
  return (targeting.domains ?? []).reduce<{
    included: Array<{ id: string; url: string }>;
    excluded: Array<{ id: string; url: string }>;
  }>(
    (result, domain) => {
      const normalized = normalizePersistedDomain(domain);
      if (
        domain &&
        typeof domain === 'object' &&
        'excluded' in domain &&
        domain.excluded === true
      ) {
        result.excluded.push(normalized);
      } else {
        result.included.push(normalized);
      }
      return result;
    },
    { included: [], excluded: [] },
  );
}

export function normalizeLegacyLiverampSegments(value: unknown) {
  if (!value || typeof value !== 'object') {
    return { id: '', excluded: false, segments: [] as LiverampSegment[] };
  }
  const record = value as Record<string, unknown>;
  const result = z.array(persistedLiverampSegmentSchema).safeParse(record['lrSegments'] ?? []);
  return {
    id:
      typeof record['id'] === 'string' || typeof record['id'] === 'number'
        ? String(record['id'])
        : '',
    excluded: record['excluded'] === true,
    segments: result.success ? result.data : [],
  };
}

export function normalizePersistedGeoLocations(
  locations: PersistedTargeting['geoLocations'],
): TargetingLocation[] {
  return (locations ?? []).map((location) => ({
    ...location,
    continentId: location.continentId ?? undefined,
    countryId: location.countryId ?? undefined,
    countryName: location.countryName ?? undefined,
    geocodeId: persistedLocationIdSchema.parse(location.geocodeId),
    regionId: persistedLocationIdSchema.parse(location.regionId),
    subRegionId: persistedLocationIdSchema.parse(location.subRegionId),
    subdivision1Name: location.subdivision1Name ?? undefined,
    subdivision2Name: location.subdivision2Name ?? undefined,
    locationName: location.locationName ?? undefined,
    tag: location.tag ?? undefined,
  }));
}

function normalizePersistedDomain(domain: unknown) {
  if (typeof domain === 'string') return { id: domain, url: domain };
  if (!domain || typeof domain !== 'object') return { id: '', url: '' };
  const record = domain as Record<string, unknown>;
  const id = persistedScalarString(record['id'] ?? record['url'] ?? record['domain']);
  return {
    ...record,
    id,
    url: persistedScalarString(record['url'] ?? record['domain']) || id,
  };
}

function persistedScalarString(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}
